/**
 * checkout-disclosures — event-page's own checkout-time legal disclosures,
 * mounted twice per checkout with two different jobs and two different
 * spatial requirements.
 *
 * `priceOnly` (event-page.view.ts mounts this variant immediately above the
 * whole payment block — the wallet express-pay buttons included, since a
 * tap there concludes the order in one gesture): the § 312j Abs. 2 BGB
 * statement of the total price including VAT, given "unmittelbar bevor der
 * Verbraucher seine Bestellung abgibt … in hervorgehobener Weise" — carried
 * by type scale rather than colour: `label/lg` at full on-surface tone is the
 * one fine-print line on the page that is not dimmed. The line leads with the
 * Gesamtpreis — the sum of the basket's gross line amounts — because it is the
 * only statement of the total a buyer tapping the wallet button ever sees: the
 * pay CTA carries it too, but on a phone that label sits below the divider and
 * the card field, off the first screen. The statute's other two facts already
 * sit above the button on their own — essential characteristics as the basket
 * rows themselves, and there is no minimum contract term to disclose for a
 * one-off ticket purchase. A single shared rate names the percentage
 * ("63,00 € inkl. 7 % MwSt."); a basket mixing rates (a workshop add-on taxed
 * differently from the ticket itself) states inclusion without a figure, since
 * no single percentage would be honest there and the itemised amount is § 14
 * UStG invoice content the receipt delivers, not the checkout's. Renders
 * nothing once the basket is free, since there is no price to state.
 *
 * `priceOnly=false` (the default — both event-page.view.ts mount sites use
 * it unchanged): the trust footnote mounted directly under the CTA — the
 * terms clause ("Mit »Bezahlen« akzeptierst du unsere AGB." — the § 305 BGB
 * reference plus the link that makes them readable before the contract
 * closes, quoting the button's own verb so the sentence names the gesture the
 * buyer is about to make), the Privacy Policy link (Art. 13 GDPR: name+email
 * is collected at checkout, so the document must be reachable here — a bare
 * link, never "you agree", since the lawful basis is Art. 6(1)(b) contract,
 * not consent), and the imprint link (§ 5 DDG: the checkout route mounts no
 * `<legal-footer>`, so this is the only imprint access on the page the
 * contract is actually concluded on — gated on `imprintAvailable`, the same
 * incomplete-`legal` withholding legal-footer.view.ts's own imprint link
 * applies, since a fabricated statutory disclosure is worse than a withheld
 * one). Between the terms clause and those links sits the ad-report cue, and
 * only where the organiser has a Meta pixel registered and Meta's own script
 * off (`metaAdsTier`): in that tier the address the buyer just typed is
 * hashed and sent to an ad platform after the purchase on Art. 6(1)(f) with
 * nothing asked of them, and a fact carried only by a policy the buyer never
 * opens is not a fact they were told — one sentence naming what is sent, who
 * receives it and that they may object, with the privacy link directly under
 * it carrying the rest. The accepting verb reaches the terms clause and
 * nothing else: the cue, privacy and imprint are statements and bare links,
 * so none ever reads as something the buyer is accepting. The client legal
 * cluster stays flush right while the platform maker's signature mirrors it
 * at the left edge of the same line band — separate identities without adding
 * another row below the CTA.
 *
 * Every link reuses the exact mechanism sign-consent.view.ts's own consent
 * gate and legal-footer.view.ts's own footer use to open the same
 * documents: `legalDocumentFor` (legal.state.ts) resolves the terms/privacy
 * document (the operator's published override once one is fully published,
 * else the platform default) from the public workspaceCard (already
 * hydrated on the storefront for `<brand-logo>`) plus workspaceDomain.get(),
 * falling back to the catalog's placeholder text so the click never
 * produces nothing before the card resolves; the imprint link instead opens
 * `<legal-imprint>` straight off `Workspace.legal` — the same component the
 * public /legal page embeds — since an imprint isn't a resolved
 * LegalDocument. Neither link names a layout: `show()` measures the mounted
 * document against the room the viewport has and settles it itself — a
 * centered card at desktop width, a bottom sheet below it — so a mobile
 * buyer reading the full document gets the sheet's native swipe-to-dismiss
 * and full-height room with no presentation decision made here.
 *
 * Each link's `href` is a real, resolvable `/legal#<kind>` URL
 * (`legalAnchorHref`) — never `href="#"` — so it is crawlable, shareable,
 * and a modified click (⌘/ctrl/shift/a non-primary button,
 * `isPlainLeftClick`) falls through to the real navigation instead of the
 * overlay; only a plain left click is intercepted.
 */

import { formatCurrency } from "@mhmo91/rx";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import { html } from "lit";
import { property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { takeUntil } from "rxjs";
import { LocalizeMixin } from "../i18n/localize.ts";
import { workspaceDomain } from "../workspace/workspace.state.ts";
import "../legal/legal-document.view.ts";
import "../legal/legal-imprint.view.ts";
import { metaAdsTier } from "@momo/i18n";
import {
	deploymentCapabilities,
	imprintAvailable,
	imprintIdentity,
	isPlainLeftClick,
	type LegalAnchor,
	legalAnchorHref,
	legalDocumentFor,
} from "../legal/legal.state.ts";
import "./revia-logo.view.ts";

/** The one slice of an order line this footer needs — every field named for
 *  its Order.OrderLine (@momo/types) counterpart, so a caller passes
 *  orderLines.get() straight through. */
export type DisclosureLine = {
	readonly vatRate: number;
	readonly amount: number;
};

@schmancyElement("checkout-disclosures")
export class CheckoutDisclosures extends LocalizeMixin(SchmancyElement) {
	@property({ attribute: false }) lines: readonly DisclosureLine[] = [];

	/** True renders only the § 312j Abs. 2 price-incl.-VAT statement — the
	 *  variant mounted immediately above the payment block. False (default)
	 *  renders the terms/privacy/imprint footnote mounted under the CTA. */
	@property({ type: Boolean }) priceOnly = false;

	/** What an applied voucher code takes off `lines`, positive cents — the
	 *  server's own per-VAT-rate discount lines summed. Subtracted here rather
	 *  than handed in as a negative line, and never shown beside the total: the
	 *  statute asks for the Gesamtpreis "in hervorgehobener Weise", and a
	 *  struck-through gross next to it leaves the buyer working out which of two
	 *  figures they are being asked to pay. What the code did is confirmed in
	 *  the entry overlay, at the moment they applied it. The named VAT rate is
	 *  read off the positive lines alone, so a discount never adds a rate to a
	 *  single-rate basket. */
	@property({ type: Number }) discountCents = 0;

	/** Opens the terms or privacy document in an overlay — the exact
	 *  mechanism sign-consent.view.ts's own consent gate uses for both. */
	private openLegalDocument(which: "terms" | "privacy") {
		const { document, identityUnavailable } = legalDocumentFor(
			which,
			this.uiLanguage,
			workspaceDomain.get(),
			this.t("legal.identityUnavailable"),
		);
		show(
			() => html`
				<legal-document
					.document=${document}
					?identityUnavailable=${identityUnavailable}
				></legal-document>
			`,
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	/** One document's link — labelled, real-`href`, opened by `open` on a
	 *  plain left click only (`isPlainLeftClick`); a modified click
	 *  (⌘/ctrl/shift/a non-primary button) falls through to the href's own
	 *  navigation. Shared by all three links this footer renders — terms
	 *  and privacy open the resolved-document overlay, imprint opens
	 *  `<legal-imprint>` straight off `Workspace.legal`. */
	private legalLink(anchor: LegalAnchor, label: string, open: () => void) {
		return html`<a
			href=${legalAnchorHref(anchor)}
			class="font-semibold text-primary-default hover:underline"
			@click=${(e: MouseEvent) => {
				if (!isPlainLeftClick(e)) return;
				e.preventDefault();
				open();
			}}
		>${label}</a>`;
	}

	override render() {
		if (this.priceOnly) {
			const total =
				this.lines.reduce((sum, l) => sum + l.amount, 0) - this.discountCents;
			if (total <= 0) return html``;
			const rates = new Set(
				this.lines.filter((l) => l.amount > 0).map((l) => l.vatRate),
			);
			const formattedTotal = formatCurrency(total / 100, "EUR", this.locale);
			return html`
				<schmancy-typography type="label" token="lg" align="right" class="text-pretty">
					${
						rates.size <= 1
							? this.t(
									"storefront.eventPage.payArea.disclosures.totalSingleRate",
									{
										total: formattedTotal,
										rate: [...rates][0] ?? 0,
									},
								)
							: this.t(
									"storefront.eventPage.payArea.disclosures.totalMixedRate",
									{ total: formattedTotal },
								)
					}
				</schmancy-typography>
			`;
		}

		// `total` picks the same branch the button does — pay or free-order —
		// so the quoted verb always names the order actually being placed, and
		// switches with it the moment the basket goes free. It deliberately
		// does not track the label's transient tenses (the settling whisper
		// while the amount catches up, a retry relabel after a refusal):
		// § 305 asks the clause to name the act, not to mirror whatever
		// characters the button is currently showing.
		const total =
			this.lines.reduce((sum, l) => sum + l.amount, 0) - this.discountCents;
		// The terms link is spliced in exactly where the catalog's own
		// `{{terms}}` placeholder puts it — never a hardcoded suffix a copy
		// edit could silently strand.
		const [prefix, suffix] = this.t(
			"storefront.eventPage.payArea.disclosures.terms",
			{
				action: this.t(
					total > 0
						? "storefront.eventPage.payArea.payVerb"
						: "storefront.eventPage.payArea.freeOrder",
				),
			},
		).split("{{terms}}");

		// The Art. 13 cue at the point of collection, in the server-report tier
		// only: there the buyer's typed address is hashed and sent to Meta on
		// Art. 6(1)(f), with nothing asked of them, so the fact belongs where
		// they type it and not only in a document they may never open. The
		// browser tier is deliberately silent here — nothing is sent there
		// without a consent prompt, which is its own notice, and a sentence
		// written for the non-consent basis would misdescribe it. Same tier
		// fact the privacy document's own Meta disclosure reads
		// (`metaAdsTier`), so the two can never disagree.
		const adsCue =
			metaAdsTier(deploymentCapabilities()) === "serverReport"
				? this.t("storefront.eventPage.payArea.disclosures.adsReport")
				: null;

		return html`
			<div class="relative">
				<div class="grid justify-items-end gap-0.5">
					<schmancy-typography type="body" token="sm" align="right" class="text-surface-onVariant text-pretty">
					${prefix}${this.legalLink("terms", this.t("legal.footer.terms"), () => this.openLegalDocument("terms"))}${suffix}
					</schmancy-typography>
					${when(
						adsCue,
						() => html`
							<schmancy-typography type="body" token="sm" align="right" class="text-surface-onVariant text-pretty">
								${adsCue}
							</schmancy-typography>
						`,
					)}
					<div class="flex items-stretch justify-end gap-3">
						<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
						${this.legalLink("privacy", this.t("legal.footer.privacy"), () => this.openLegalDocument("privacy"))}
						</schmancy-typography>
						${when(
							imprintAvailable.get(),
							() => html`
								<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
								${this.legalLink(
									"imprint",
									this.t("legal.footer.imprint"),
									() => {
										const legal = imprintIdentity.get();
										if (!legal) return;
										show(
											() =>
												html`<legal-imprint .legal=${legal}></legal-imprint>`,
										)
											.pipe(takeUntil(this.disconnecting))
											.subscribe();
									},
								)}
								</schmancy-typography>
							`,
						)}
					</div>
				</div>
				<revia-logo class="absolute bottom-0 left-0"></revia-logo>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"checkout-disclosures": CheckoutDisclosures;
	}
}
